<template>
  <div :class="'p' + roundedValue" class="c100 center" @click="goToSearchGatewayForm">
    <span>{{ value | number('0','','%') }}</span>
    <div class="slice">
      <div class="bar" />
      <div class="fill" />
    </div>
  </div>
</template>

<script>
import { result, find } from 'lodash-es'

export default {
  props: {
    // Total of gateways to calculate %
    totalCount: {
      type: Number,
      required: true
    },
    // Last event counters for alarms
    lastEventsCounters: {
      type: Object,
      required: true
    },
    // Attribute to look for values in lastEventsCounters
    attribute: {
      type: String,
      required: true
    }
  },

  computed: {
    value () {
      if (this.totalCount === 0) return 0

      return result((find(this.lastEventsCounters.counters, { type: 'ALARM', name: this.attribute }) || { count: 0 }), 'count') / this.totalCount * 100
    },

    roundedValue () {
      return Math.round(this.value)
    }
  },

  methods: {
    goToSearchGatewayForm () {
      const filters = { alarms: {} }
      filters.alarms[this.attribute] = true
      this.$store.commit('setGatewaysFilters', filters)

      this.$router.push('/gateways')
    }
  }
}
</script>

<style scoped lang="scss">
  // From https://github.com/afuersch/css-percentage-circle
  @import "~@/scss/variables";

  // VARS
  $circle-width: 0.1em;
  $circle-width-hover: 0.05em;

  // colors default
  $primary-color: $cyan;
  $secondary-color: $gray-300;
  $bg-color: $white;

  // CIRCLE
  // classes 2 extend
  .rect-auto {
    clip: rect(auto, auto, auto, auto);
  }

  .pie {
    position: absolute;
    width: 1 - (2 * $circle-width);
    height: 1 - (2 * $circle-width);
    clip: rect(0, 0.5em, 1em, 0);
    border: $circle-width solid $primary-color;
    border-radius: 50%;
    transform: rotate(0deg);
  }

  .pie-fill {
    transform: rotate(180deg);
  }

  // main
  .c100 {
    *,
    *::after,
    *::before {
      box-sizing: content-box;
    }

    position: relative;
    float: left;
    width: 1em;
    height: 1em;
    margin: 0 0.1em 0.1em 0;
    font-size: 120px;
    background-color: $secondary-color;
    border-radius: 50%;

    // center circle to its parent
    &.center {
      float: none;
      margin: 0 auto;
    }

    // bigger size
    &.big {
      font-size: 240px;
    }

    // smaller size
    &.small {
      font-size: 80px;
    }

    // centered value inside circle
    > span {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      display: block;
      width: 100%;
      width: 5em;
      font-size: 0.2em;
      line-height: 5em;
      color: $primary-color;
      text-align: center;
      white-space: nowrap;
      transition-timing-function: ease-out;
      transition-duration: 0.2s;
      transition-property: all;
    }

    // background inside the circle
    &::after {
      position: absolute;
      top: $circle-width;
      left: $circle-width;
      display: block;
      width: 1 - (2 * $circle-width);
      height: 1 - (2 * $circle-width);
      content: " ";
      background-color: $bg-color;
      border-radius: 50%;
      transition-timing-function: ease-in;
      transition-duration: 0.2s;
      transition-property: all;
    }

    // the slice (mask)
    .slice {
      position: absolute;
      width: 1em;
      height: 1em;
      clip: rect(0, 1em, 1em, 0.5em);
    }

    // circle to show the status
    .bar {
      @extend .pie;

      background: none;
      transition-timing-function: ease-in;
      transition-duration: 0.2s;
      transition-property: all;
    }

    // loop to create all needed elements automatically
    @for $j from 51 through 100 {
      &.p#{$j} {
        .slice {
          @extend .rect-auto;
        }

        .bar::after {
          @extend .pie-fill;
        }

        .fill {
          @extend .pie;
          @extend .pie-fill;
        }
      }
    }

    // loop to rotate all 100 circles
    @for $j from 1 through 100 {
      &.p#{$j} .bar {
        transform: rotate((360/100*$j) + deg);
      }
    }

    // hover styles
    &:hover {
      cursor: pointer;

      > span {
        width: 3.33em;
        font-size: 0.3em;
        line-height: 3.33em;
        color: $primary-color;
      }

      &::after {
        top: $circle-width-hover;
        left: $circle-width-hover;
        width: 1 - (2 * $circle-width-hover);
        height: 1 - (2 * $circle-width-hover);
      }
    }
  }
</style>
